import { FuseAnimate } from '@fuse'
import { Icon, CircularProgress, Link } from '@material-ui/core'
import {
  TextInput,
  ButtonWithLoading,
} from 'app/components/FormsyHelperComponent'
import Loader from 'app/components/Loader'
import PhoneNumberInputFormsy, {
  validatePhoneNumber,
} from 'app/components/PhoneNumberInputFormsy'
import ProfileAvatarFormsy from 'app/main/notification/components/ProfileAvatarFormsy'
import EmailInputFormsy from 'app/components/EmailInputFormsy'
import {
  useCountryCode,
  parsedPhoneNumber,
  getFullPhoneNumber,
} from 'commons/phoneNumber'
import Formsy from 'formsy-react'
import PropTypes from 'prop-types'
import * as R from 'ramda'
import React, { useRef, useState } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import Assets from 'app/configs/assetConfigs'
import { useFormsy } from 'commons/form'
import CancelButton from './buttons/CancelButton'
import { isValidLink, isBase64 } from 'commons/website'
import { useDefaultLanguage } from 'commons/useDefaultLanguage'
import StatefulSelector, { validateRequired } from './selector/StatefulSelector'
import { ProgressWithText } from './progress/ProgressWithText'
import LanguageRenderItem from './selector/LanguageRenderItem'
import useLanguage from 'app/components/selector/useLanguage'
import IconInputRow, { ItemType } from 'app/components/input/IconInputRow'
import ResponsiveDialogAction from 'app/components/dialog/ResponsiveDialogAction'
import ResponsiveDialogContent from 'app/components/dialog/ResponsiveDialogContent'
import ResponsiveDialog from 'app/components/dialog/ResponsiveDialog'
import ResponsiveAppbar, {
  AppbarDisplayName,
} from 'app/components/dialog/ResponsiveAppbar'
import ConfirmDeleteAdminDialog from 'app/components/ConfirmDeleteAdminDialog/ConfirmDeleteAdminDialog'
import { useFlag } from '@unleash/proxy-client-react'
import clsx from 'clsx'

const parseAvatar = R.ifElse(
  R.o(
    R.allPass([
      R.anyPass([R.equals(Assets.personSilhouetteImage), isValidLink]),
      R.complement(isBase64),
    ]),
    R.prop('avatar'),
  ),
  R.always({ avatar: undefined }),
  R.identity,
)
const parsePhoneNumber = (value) =>
  R.defaultTo(
    { phoneNumber: undefined },
    parsedPhoneNumber(value.fullPhoneNumber),
  )

const langKey = 'langauge'
function PersonFormDialog(props) {
  const {
    isOpen,
    onClose,
    onSubmit,
    initialData = {},
    isPending,
    onDeleteAdmin,
  } = props
  const { formatMessage } = useIntl()
  const { countryCode, isGeoInfoLoading } = useCountryCode()
  const formRef = useRef()
  const { isPristine, onChange } = useFormsy(formRef)
  const isAdding = R.isEmpty(initialData)
  const getPropInitialDataOrEmptyString = R.propOr('', R.__, initialData)

  const [isOpenConfirmDelete, setIsOpenConfirmDelete] = useState(false)
  const [isFormValid, setIsFormValid] = useState()
  const disableButton = () => setIsFormValid(false)
  const enableButton = () => setIsFormValid(true)
  const onInternalSubmit = (model) => {
    const resultModel = R.mergeAll([
      R.pickAll(['firstName', 'lastName', 'email'])(model),
      parsePhoneNumber(model),
      parseAvatar(model),
      { languageId: R.path([langKey, 'id'], model) },
    ])

    onSubmit(resultModel)
  }
  const defaultLanguge = useDefaultLanguage()
  const initialLanguageId = R.prop('id', defaultLanguge)
  const { getAllLanguage } = useLanguage()

  return (
    <ResponsiveDialog
      open={isOpen}
      onClose={onClose}
      scroll="paper"
      fullWidth
      maxWidth="xs"
      removeAllSpace
    >
      <Loader
        isLoading={isGeoInfoLoading}
        placeholder={
          <FuseAnimate
            enter={{ animation: 'fadeIn' }}
            leave={{ animation: 'fadeOut' }}
          >
            <CircularProgress className="self-center m-16" />
          </FuseAnimate>
        }
      >
        <Formsy
          onChange={onChange}
          onValidSubmit={onInternalSubmit}
          onValid={enableButton}
          onInvalid={disableButton}
          className="flex flex-col overflow-hidden"
          ref={formRef}
        >
          <ResponsiveAppbar
            avatar={
              <ProfileAvatarFormsy
                className="w-96 h-96"
                alt="contact avatar"
                name="avatar"
                value={R.propOr(
                  {
                    avCharacters: initialData.avatarCharacters,
                    name: initialData.firstName,
                  },
                  'avatar',
                  initialData,
                )}
              />
            }
            displayName={
              <AppbarDisplayName>
                {`${getPropInitialDataOrEmptyString(
                  'firstName',
                )} ${getPropInitialDataOrEmptyString('lastName')}`}
              </AppbarDisplayName>
            }
          />

          <ResponsiveDialogContent>
            <IconInputRow
              itemType={ItemType.First}
              IconComponent={<Icon>account_circle</Icon>}
              Input={
                <TextInput
                  autoFocus
                  labelId="firstName.label"
                  placeholderId="firstName.label"
                  value={getPropInitialDataOrEmptyString('firstName')}
                  name="firstName"
                  required
                  maxLength={40}
                  fullWidth
                  data-testid="userDisplayFirstName"
                />
              }
            />
            <IconInputRow
              IconComponent={<Icon>account_circle</Icon>}
              Input={
                <TextInput
                  labelId="lastName.label"
                  placeholderId="lastName.label"
                  name="lastName"
                  value={getPropInitialDataOrEmptyString('lastName')}
                  required
                  maxLength={40}
                  fullWidth
                  data-testid="userDisplayLastName"
                />
              }
            />
            <IconInputRow
              IconComponent={<Icon>g_translate</Icon>}
              Input={
                <StatefulSelector
                  name={langKey}
                  label={formatMessage({ id: 'languageSelector.label' })}
                  initialItemId={initialLanguageId}
                  fetcher={getAllLanguage}
                  LoadingComp={
                    <ProgressWithText
                      label={
                        <FormattedMessage id="languageSelector.state.loading" />
                      }
                      isDisable
                    />
                  }
                  ErrorComp={() => (
                    <FormattedMessage id="languageSelector.state.error" />
                  )}
                  EmptyComp={
                    <FormattedMessage id="languageSelector.state.empty" />
                  }
                  ValidComp={(language) => (
                    <LanguageRenderItem language={language} />
                  )}
                  fullWidth
                  required
                  validations={{
                    validateRequired: validateRequired,
                  }}
                  validationErrors={{
                    validateRequired: formatMessage({
                      id: 'error.form.required',
                    }),
                  }}
                  InputProps={{
                    'data-testid': 'userDisplayAuthorLanguage',
                  }}
                />
              }
            />
            <IconInputRow
              IconComponent={<Icon>email</Icon>}
              Input={
                <EmailInputFormsy
                  labelId="email.address.label"
                  placeholderId="email.address.label"
                  name="email"
                  value={getPropInitialDataOrEmptyString('email')}
                  required
                  validations={{
                    isEmail: true,
                  }}
                  validationErrors={{
                    isEmail: formatMessage({
                      id: 'memberForm.emailInvalid',
                    }),
                  }}
                  variant="outlined"
                  fullWidth
                  InputProps={{
                    'data-testid': 'userDisplayEmailAddress',
                  }}
                />
              }
            />
            <IconInputRow
              itemType={ItemType.Last}
              IconComponent={<Icon>phone</Icon>}
              Input={
                <div className="flex-1">
                  <PhoneNumberInputFormsy
                    value={getFullPhoneNumber(
                      initialData.countryCode,
                      initialData.phoneNumber,
                    )}
                    label={formatMessage({
                      id: 'phone.label',
                    })}
                    name="fullPhoneNumber"
                    autoComplete="new-password"
                    enableSearchField
                    disableSearchIcon
                    disableAreaCodes={true}
                    defaultCountry={countryCode}
                    validations={{
                      isPhoneNumber: validatePhoneNumber,
                    }}
                    validationErrors={{
                      isPhoneNumber: formatMessage({
                        id: 'memberForm.phoneNumberInvalid',
                      }),
                    }}
                    inputExtraProps={{
                      InputProps: {
                        endAdornment: null,
                      },
                      required: false,
                    }}
                    InputProps={{
                      'data-testid': 'userDisplayPhoneNo',
                    }}
                    fullWidth
                    isScrollWhenFocus
                  />
                </div>
              }
            />
          </ResponsiveDialogContent>

          <div className={'flex justify-between'}>
            <div className="pt-32 pl-80">
              <Link
                to="#"
                onClick={() => setIsOpenConfirmDelete(true)}
                className="text-12 font-400 text-black hover:no-underline uppercase"
                role="button"
              >
                <FormattedMessage id="delete.account.lable" />
              </Link>
            </div>
            <ResponsiveDialogAction
              StartButton={
                <CancelButton onClick={onClose} className="w-full" />
              }
              EndButton={
                <ButtonWithLoading
                  className="w-full"
                  isLoading={isPending}
                  labelId={isAdding ? 'addBtn' : 'saveBtn'}
                  disabled={!isFormValid || isPending || isPristine}
                  type="submit"
                />
              }
            />
          </div>
        </Formsy>
      </Loader>
      {isOpenConfirmDelete ? (
        <ConfirmDeleteAdminDialog
          isOpen={isOpenConfirmDelete}
          onClose={() => {
            setIsOpenConfirmDelete(false)
          }}
          isLoading={isPending}
          onDelete={() => onDeleteAdmin()}
          isRootAdmin={initialData.isOwner}
          clearDialog={() => {
            setIsOpenConfirmDelete(false)
            onClose()
          }}
        />
      ) : null}
    </ResponsiveDialog>
  )
}

PersonFormDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  initialData: PropTypes.shape({
    avatar: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    email: PropTypes.string,
    phoneNumber: PropTypes.string,
  }),
}

export default PersonFormDialog
